import type { DTOOf, IBrand, IPageAxisValue, IProduct } from '@atlas/types'

/**
 * The catalogue hierarchy the product page states and steps through: Region / Market /
 * Brand / Product.
 *
 * Region and market are two levels of ONE axis (`geography`), so the page keeps carrying a
 * single scope code — a region or a country, whichever was resolved — and these split it
 * into the two controls and recombine what they pick. Brand and product are objects, not
 * axis values, so they are keyed by ID: picking a product navigates to its page, which a
 * value code could not address.
 *
 * Pure, so every level reads the same in the bar, in the breadcrumb and in the tests.
 */

/** One choice in a hierarchy select: the value the control carries, and what it reads as. */
export interface HierarchyChoice {
	value: string
	label: string
}

/** The geography scope as the two controls over it stand: the region, and the market under it. */
export interface GeoLevels {
	region: string | null
	/** `null` when the scope is a whole region — the market level is then simply unset. */
	market: string | null
}

/** The value a code names among the offered ones, or `null` when it is not offered. */
const offered = (values: IPageAxisValue[], code: string | null): IPageAxisValue | null =>
	code ? (values.find((value) => value.code === code) ?? null) : null

/**
 * The top of the tree a value sits in — its region, for a geography value.
 *
 * Walks the parent chain rather than reading `parentCode` once, so a taxonomy that grows a
 * level (a state under a country) still resolves to the region the picker offers. The
 * `seen` guard is for a self-referential parent, which would otherwise spin forever.
 */
const regionAbove = (values: IPageAxisValue[], value: IPageAxisValue): IPageAxisValue => {
	const seen = new Set<string>()
	let current = value
	while (current.parentCode !== null && !seen.has(current.code)) {
		seen.add(current.code)
		const parent = offered(values, current.parentCode)
		if (!parent) return current
		current = parent
	}
	return current
}

/** The regions on offer — the top level of the geography the server said the product reaches. */
export const regionsOf = (values: IPageAxisValue[]): IPageAxisValue[] =>
	values.filter((value) => value.parentCode === null)

/**
 * The markets on offer under a region. Empty without one: a market is only pickable inside
 * its region, which is what makes the pair a drilldown rather than two flat lists of the
 * same axis.
 */
export const marketsOf = (values: IPageAxisValue[], regionCode: string | null): IPageAxisValue[] =>
	regionCode
		? values.filter((value) => value.code !== regionCode && regionAbove(values, value).code === regionCode)
		: []

/**
 * The scope the page resolved for, split into the two levels that state it. A code the
 * server did not offer leaves both unset — it resolved nothing, so neither control may
 * claim a value.
 */
export const geoLevelsOf = (values: IPageAxisValue[], scope: string | null): GeoLevels => {
	const value = offered(values, scope)
	if (!value) return { region: null, market: null }
	const region = regionAbove(values, value)
	return { region: region.code, market: region.code === value.code ? null : value.code }
}

/** Brands as the picker offers them: the whole catalogue, by name, keyed by ID. */
export const brandChoices = (brands: DTOOf<IBrand>[]): HierarchyChoice[] =>
	brands.map((brand) => ({ value: brand.brandId, label: brand.name })).sort((a, b) => a.label.localeCompare(b.label))

/** The products of one brand, by name. Empty without a brand — a product belongs to one. */
export const productChoices = (products: DTOOf<IProduct>[], brandId: string | null): HierarchyChoice[] =>
	brandId
		? products
				.filter((product) => product.brandId === brandId)
				.map((product) => ({ value: product.productId, label: product.name }))
				.sort((a, b) => a.label.localeCompare(b.label))
		: []
